import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Droplets,
  Waves,
  Sparkles,
  Check,
  MessageCircle,
  ArrowRight,
  ShieldCheck,
  Leaf,
} from "lucide-react";
import { MESSENGER_URL } from "@/constants/content";
import { useSiteContent } from "@/hooks/useSiteContent";

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };

const PILLAR_ICONS = [Droplets, Waves, Sparkles];

export default function ForeverBeauty() {
  const { content } = useSiteContent();
  const PILLARS = content.foreverPillars || [];
  const PACKAGES = content.foreverPackages || [];

  return (
    <div data-testid="forever-page" className="text-[#1A2825]">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-8 md:pb-12">
        <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">
            Forever Living · Arc- és szépségápolás
          </span>
          <h1 className="font-serif-display text-5xl md:text-6xl font-light text-[#1A2825] mt-4 leading-[1.04]">
            A ragyogás <em className="not-italic text-[#2A9D8F]">rutinból</em> épül.
          </h1>
          <p className="mt-6 text-lg text-[#3A4D4A] font-light leading-relaxed">
            A szép bőr titka nem egyetlen csodakrém, hanem a következetes rendszer. A Forever
            aloe verára épülő termékei egymást felerősítve dolgoznak – három egyszerű lépésben.
          </p>
          <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-wider text-[#758C88]">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#2A9D8F]" /> Aloe vera alapokon</span>
            <span className="inline-flex items-center gap-1.5"><Leaf className="h-3.5 w-3.5 text-[#2A9D8F]" /> Bőrbarát formulák</span>
          </div>
        </motion.div>
      </section>

      {/* 3 pillér */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-14 md:pb-20">
        <div className="text-center mb-10">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">A rutin 3 pillére</span>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] mt-3">
            Tisztítás · Tonizálás · Hidratálás
          </h2>
        </div>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[i] || Sparkles;
            const step = String(i + 1);
            return (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`forever-pillar-${step}`}
                className="relative bg-[#F6F3EB]/80 rounded-[1.75rem] border border-[#EAE5D9] p-7 text-center"
              >
                <div className="mx-auto h-14 w-14 rounded-2xl bg-white border border-[#2A9D8F]/25 flex items-center justify-center">
                  <Icon className="h-6 w-6 text-[#2A9D8F]" />
                </div>
                <div className="mt-4 font-serif-display text-5xl text-[#D4AF37]/40 leading-none">{step}</div>
                <h3 className="font-serif-display text-xl text-[#1A2825] mt-2">{p.title}</h3>
                <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed">{p.text}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* Csomagok */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="mb-10">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">Csomagok</span>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] mt-3">
            Válaszd a hozzád illő teljes rutint
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {PACKAGES.map((p, i) => (
            <motion.article
              key={p.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              data-testid={`forever-package-${p.key}`}
              className="relative bg-white rounded-[2rem] border overflow-hidden flex flex-col shadow-[0_8px_32px_rgba(212,175,55,0.08)]"
              style={{ borderColor: `${p.accent}40` }}
            >
              <div className="h-2" style={{ background: `linear-gradient(90deg, ${p.accent}, ${p.accent}80)` }} />
              {p.cover && (
                <div className="h-48 overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    data-testid={`forever-package-cover-${p.key}`}
                  />
                </div>
              )}
              <div className="p-7 md:p-8 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: p.accent }}>Forever</div>
                <h3 className="font-serif-display text-2xl md:text-[26px] text-[#1A2825] mt-2 leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm italic" style={{ color: p.accent }}>{p.tagline}</p>
                <p className="mt-5 text-[#3A4D4A] font-light leading-relaxed text-[15px]">{p.description}</p>

                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#758C88] mb-2">A csomag tartalma</div>
                  <ul className="space-y-2 text-sm text-[#3A4D4A] font-light">
                    {(p.contents || []).map((c) => (
                      <li key={c} className="flex items-start gap-2">
                        <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: p.accent }} />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5 rounded-2xl p-4" style={{ background: `${p.accent}0F` }}>
                  <span className="font-medium text-[#1A2825] text-sm">Mire jó? </span>
                  <span className="text-sm text-[#3A4D4A] font-light leading-relaxed">{p.benefit}</span>
                </div>

                <div className="mt-auto pt-7 flex flex-col gap-3">
                  <a
                    href={MESSENGER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`forever-order-btn-${p.key}`}
                    className="inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full font-medium tracking-wide transition-all shadow-md hover:-translate-y-0.5"
                    style={{ background: p.accent }}
                  >
                    <MessageCircle className="h-4 w-4" /> Bővebb infó vagy rendelés
                  </a>
                  <Link
                    to={`/blog?article=${p.articleKey || "borapolas-szinergia"}`}
                    data-testid={`forever-article-link-${p.key}`}
                    className="inline-flex items-center justify-center gap-2 text-sm font-medium text-[#1C6B61] hover:text-[#2A9D8F] transition-colors"
                  >
                    Elolvasom a teljes cikket <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
